import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-number-input/input";
import {
  Form,
  Input,
  Button,
  Space,
  message,
  Flex,
  DatePicker,
  Select,
  Checkbox,
  Spin,
  Alert,
  Grid,
} from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";

import PageTitle from "../../../Components/PageTitle/pageTitle";

import userServices from "../../../APIs/userServices";
import authCheck from "../../../APIs/authCheck";

import { settings } from "../../../Services/apiSetting";

const { useBreakpoint } = Grid;

const Register = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const [messageApi, contextHolder] = message.useMessage();

  const [isLoading, setIsLoading] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);

  const [errorMessage, setErrorMessage] = useState("");

  const [hpNumberError, setHpNumberError] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");

  const [form] = Form.useForm();

  useEffect(() => {
    onFormChange();
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        const response = await authCheck.authCheck();

        if (response?.status === 200) navigate("/profile");
        else setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  const showError = (error) => {
    messageApi.open({
      key: "registration-error",
      type: "error",
      content: error.response?.data.message || "Registration failed!",
      duration: 10,
    });
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const params = {
        firstName: values.firstName,
        lastName: values.lastName,
        // password: values.password,
        emailAddress: values.emailAddress,
        phoneNumber: values.phoneNumber,
        company: values.company,
        dateOfBirth: values.dateOfBirth,
        userAddresses: [
          {
            addressOne: values.address,
            addressTwo: "",
            postCode: values.zipCode,
            city: values.city,
            state: values.state,
            country: values.country,
          },
        ],
      };

      const response = await userServices.register(params);

      // console.log("Registration response:", response);

      if (response.status === 200) {
        messageApi.success("Registration successful!");

        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        messageApi.error(response.data.message || "Registration failed!");

        setErrorMessage(response.data.message || "Registration failed!");

        // console.log("Registration error:", response.data);
      }
    } catch (error) {
      console.error("Registration error:", error.response?.data || error);

      setErrorMessage(error.response?.data.message || "Registration failed!");

      showError(error);

      setIsLoading(false);
    }
  };

  const onFormChange = () => {
    const values = form.getFieldsValue();

    const requiredFields = [
      "firstName",
      "lastName",
      // "password",
      "dateOfBirth",
      "phoneNumber",
      "emailAddress",
      "address",
      "zipCode",
      "country",
      "city",
      "state",
      "company",
      "agreement",
    ];

    const isAnyFieldEmpty = requiredFields.some((field) => !values[field]);

    setIsButtonDisabled(isAnyFieldEmpty);
  };

  return (
    <Space direction="vertical" className="page-container">
      {isLoading && (
        <Spin
          spinning={isLoading}
          indicator={<LoadingOutlined spin />}
          fullscreen
        />
      )}

      {contextHolder}

      <PageTitle
        title="Create Your Teamstar Account"
        subTitle="Register to track your renovations, manage purchases, and access exclusive content."
      />

      <Form
        form={form}
        autoComplete="off"
        name="register"
        initialValues={{ remember: true }}
        onFinish={onFinish}
        onValuesChange={onFormChange}
        layout="vertical"
        style={{
          maxWidth: screens.xs ? "100%" : 600,
          margin: "0 auto",
          padding: screens.xs ? 10 : 20,
          paddingBottom: 10,
        }}
      >
        <Flex gap={10}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="First Name" />
          </Form.Item>

          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="Last Name" />
          </Form.Item>
        </Flex>

        {/* Phone Number and Date of Birth */}
        <Flex gap={10}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginBottom: 0,
              flex: 1,
            }}
          >
            <Form.Item
              label="Phone Number"
              name="phoneNumber"
              rules={[{ required: true, message: "" }]}
              hasFeedback
              style={{ width: "100%" }}
            >
              <PhoneInput
                className="custom-phone-input ant-input"
                country="MY"
                international
                withCountryCallingCode
                placeholder="+60"
                value={phoneValue}
                onChange={(value) => {
                  const isValid = /^\+?\d*$/.test(value || "");

                  if (isValid) {
                    setPhoneValue(value);
                  } else {
                    setPhoneValue(""); // clear it if invalid (letter entered)
                  }
                }}
                style={{
                  width: "100%",
                  borderRadius: 6,
                  border:
                    hpNumberError &&
                    form.getFieldError("phoneNumber").length === 0
                      ? "1px solid #ff4d4f"
                      : "1px solid #d9d9d9",
                  padding: "7px 11px",
                  fontSize: 14,
                }}
              />
            </Form.Item>

            {(hpNumberError || /^\+\d+$/.test((phoneValue || "").trim())) && (
              <span
                style={{
                  position: "absolute",
                  right: 13,
                  top: 36,
                  pointerEvents: "none",
                }}
              >
                {hpNumberError ? (
                  <CloseCircleFilled
                    style={{ color: "#ff4d4f", fontSize: 14 }}
                  />
                ) : (
                  <CheckCircleFilled
                    style={{ color: "#52c41a", fontSize: 14 }}
                  />
                )}
              </span>
            )}
          </span>
        </Flex>

        <Form.Item
          label="Date of Birth"
          name="dateOfBirth"
          rules={[{ required: true, message: "" }]}
          hasFeedback
          style={{ flex: 1 }}
        >
          <DatePicker
            style={{
              width: "100%",
            }}
            showNow={false}
            disabledDate={(current) => current && current > new Date()}
            placeholder="Select Date"
          />
        </Form.Item>

        <Flex>
          <Form.Item
            label="Email Address"
            name="emailAddress"
            rules={[
              {
                required: true,
                type: "email",
                message: "",
              },
            ]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="Email" />
          </Form.Item>
        </Flex>

        <Flex>
          <Form.Item
            label="Address"
            name="address"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input.TextArea placeholder="Address" />
          </Form.Item>
        </Flex>

        {/* Zip Code and Country */}
        <Flex gap={10}>
          <Form.Item
            label="Zip Code"
            name="zipCode"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="Zip Code" />
          </Form.Item>

          <Form.Item
            label="Country"
            name="country"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Select placeholder="Select your country">
              <Select.Option value="MY">Malaysia</Select.Option>
            </Select>
          </Form.Item>
        </Flex>

        {/* City and State */}
        <Flex gap={10}>
          <Form.Item
            label="City"
            name="city"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="City" />
          </Form.Item>

          <Form.Item
            label="State"
            name="state"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Select placeholder="Select your state">
              <Select.Option value="Selangor">Selangor</Select.Option>
              <Select.Option value="Kuala Lumpur">Kuala Lumpur</Select.Option>
              <Select.Option value="Penang">Penang</Select.Option>
              <Select.Option value="Johor">Johor</Select.Option>
              <Select.Option value="Sabah">Sabah</Select.Option>
              <Select.Option value="Sarawak">Sarawak</Select.Option>
              <Select.Option value="Melaka">Melaka</Select.Option>
              <Select.Option value="Negeri Sembilan">
                Negeri Sembilan
              </Select.Option>
              <Select.Option value="Perak">Perak</Select.Option>
              <Select.Option value="Pahang">Pahang</Select.Option>
              <Select.Option value="Kedah">Kedah</Select.Option>
              <Select.Option value="Kelantan">Kelantan</Select.Option>
              <Select.Option value="Terengganu">Terengganu</Select.Option>
              <Select.Option value="Perlis">Perlis</Select.Option>
              <Select.Option value="Putrajaya">Putrajaya</Select.Option>
              <Select.Option value="Labuan">Labuan</Select.Option>
            </Select>
          </Form.Item>
        </Flex>

        <Flex>
          <Form.Item
            label="Company Name"
            name="company"
            rules={[{ required: true, message: "" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Input placeholder="Company Name" />
          </Form.Item>
        </Flex>

        <Flex>
          <Form.Item
            name="agreement"
            valuePropName="checked"
            rules={[{ required: true, message: "You must accept the terms" }]}
            hasFeedback
            style={{ flex: 1 }}
          >
            <Checkbox style={{ color: "#4B5563" }}>
              I agree to the{" "}
              <a
                href={settings.filesFolder + "Privacy-policy.pdf"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href={settings.filesFolder + "Terms-of-use.pdf"}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
            </Checkbox>
          </Form.Item>
        </Flex>

        {errorMessage && (
          <Form.Item>
            <Alert message={errorMessage} type="error" showIcon>
              {errorMessage}
            </Alert>
          </Form.Item>
        )}

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={isButtonDisabled || isLoading}
            style={{
              backgroundColor: isButtonDisabled ? "#BFBFBF" : "#002753",
              width: "100%",
              height: 40,
              color: "#FFFFFF",
            }}
          >
            Register
          </Button>
        </Form.Item>
      </Form>

      <p
        style={{
          color: "#4B5563",
          textAlign: "center",
        }}
      >
        Already have an account?{" "}
        <span
          onClick={() => navigate("/login")}
          style={{ color: "#002753", cursor: "pointer", fontWeight: "bold" }}
        >
          Log in
        </span>
      </p>
    </Space>
  );
};

export default Register;
